import React, { useState, useCallback } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useSelector, useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import EmailInput from "components/input/EmailInput.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { EmailProps, EmailActionData } from "types/modals";
import { Notification, NotificationType } from "components/common/Notification.component";
import { Checkbox } from "components/common/checkbox.component";
import { Button, ButtonType } from "components/common/Button.component";
import { FormField } from "components/common/FormField.component";
import { TextInput } from "components/common/TextInput.component";
import { emailTask } from "components/modals/modal.actions";
import * as reduxSelectors from "components/store/application.reducers";
import { Store } from "redux";

const EmailModal = ({ more, closeModal, loopFocus, errorCallback, initialFocusRef }: EmailProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const loggedInData = useSelector((store: Store) => reduxSelectors.getUsersLoggedInData(store));

    const firstTask = more.tasks[0];
    const senderName = firstTask.supplierName ? firstTask.supplierName : firstTask.requesterName;
    const fromField = `${translate("popUp.email.from")} ${senderName}`;
    const initialSubject = ` ${fromField} : ${firstTask.description}`;

    const [to, setTo] = useState("");
    const [cc, setCc] = useState("");
    const [bcc, setBcc] = useState("");
    const [subject, setSubject] = useState(initialSubject);
    const [sendAttachment, setSendAttachment] = useState(true);
    const [sendCopy, setSendCopy] = useState(true);
    const [comment, setComment] = useState("");
    const [actionRunning, setActionRunning] = useState(false);
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);
    const [showCcInput, setShowCcInput] = useState(false);
    const [showBccInput, setShowBccInput] = useState(false);

    const myEmail = more.loggedInData?.emailAddress ?? loggedInData?.emailAddress ?? "";

    const handleSuccess = useCallback(() => {
        closeModal();
    }, [closeModal]);

    const handleError = useCallback(
        (error: Record<string, any>) => {
            setDisplayError(error.error ?? error);
            setActionRunning(false);
        },
        [],
    );

    const handleSend = useCallback(() => {
        if (!to || !comment) return;

        setActionRunning(true);
        setDisplayError(null);
        const data: EmailActionData = {
            key: firstTask.key,
            to,
            comment,
            subject,
            sendAttachment,
            sendCopy,
            cc,
            bcc,
            sendDocument: more.sendDocument,
            companyId: more.companyId,
        };

        dispatch(emailTask(data, handleSuccess, handleError));
    }, [to, comment, firstTask, subject, sendAttachment, sendCopy, cc, bcc, more, dispatch, handleSuccess, handleError]);

    const handleToChange = useCallback((value: string) => {
        setTo(value);
        setDisplayError(null);
    }, []);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setDisplayError(null);
    }, []);

    const handleSubjectChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSubject(event.target.value);
        setDisplayError(null);
    }, []);

    const okEnabled = !!(to && comment && !actionRunning);
    const ccButtonsVisible = !showCcInput || !showBccInput;

    return (
        <>
            <div className="ga-modal__content">
                <div className="my-3">
                    <Notification
                        type={NotificationType.INFORMATION}
                        description={translate("popUp.email.placeholder")}
                    />
                </div>

                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}

                <div className="row w-100 m-0 p-0">
                    <div className={ccButtonsVisible ? "col-md-9 p-0" : "col-md-12 p-0"}>
                        <FormField
                            id="to"
                            label={translate("popUp.email.to")}
                            labelState={`(${translate("popUp.required")})`}
                        >
                            <EmailInput
                                focusRef={initialFocusRef}
                                className="w-100 p-0 position-relative"
                                thisId="to"
                                tabIndex={MODAL_CONTENT_TAB_INDEX}
                                autoFocus={true}
                                propagateValue={handleToChange}
                                translate={translate}
                                value={to}
                            />
                        </FormField>
                    </div>
                    {ccButtonsVisible && (
                        <div className="col-md-3 p-0 text-end mb-16 align-self-end d-flex gap-8 justify-content-end">
                            {!showCcInput && (
                                <Button
                                    type={ButtonType.SECONDARY}
                                    tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                                    onClick={() => setShowCcInput(true)}
                                    text={translate("popUp.email.cc")}
                                />
                            )}
                            {!showBccInput && (
                                <Button
                                    type={ButtonType.SECONDARY}
                                    tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                                    onClick={() => setShowBccInput(true)}
                                    text={translate("popUp.email.bcc")}
                                />
                            )}
                        </div>
                    )}
                </div>

                {showCcInput && (
                    <FormField id="cc" label={translate("popUp.email.cc")}>
                        <EmailInput
                            className="w-100 p-0 position-relative"
                            thisId="cc"
                            propagateValue={(value: string) => setCc(value)}
                            value={cc}
                            translate={translate}
                            tabIndex={MODAL_CONTENT_TAB_INDEX + 3}
                        />
                    </FormField>
                )}

                {showBccInput && (
                    <FormField id="bcc" label={translate("popUp.email.bcc")}>
                        <EmailInput
                            className="w-100 p-0 position-relative"
                            thisId="bcc"
                            propagateValue={(value: string) => setBcc(value)}
                            value={bcc}
                            translate={translate}
                            tabIndex={MODAL_CONTENT_TAB_INDEX + 4}
                        />
                    </FormField>
                )}

                <FormField
                    id="subject"
                    label={translate("popUp.email.subject")}
                    labelState={`(${translate("popUp.required")})`}
                >
                    <TextInput
                        id="subjectPlaceholder"
                        value={subject}
                        placeholder={translate("popUp.email.subjectPlaceholder")}
                        onChange={handleSubjectChange}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 5}
                    />
                </FormField>

                <Comment
                    inputHint={translate("popUp.email.messagePlaceholder")}
                    inputLimit={0}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 6}
                    id="comment"
                    hideInitialValue={true}
                    propagateValue={handleCommentChange}
                    label={translate("popUp.email.commentLabel")}
                    labelState={`(${translate("popUp.required")})`}
                />

                <Checkbox
                    inputId="attachDoc"
                    onChange={(event) => {
                        event.stopPropagation();
                        const input = (event.currentTarget as HTMLElement).querySelector("input");
                        if (input) {
                            setSendAttachment(input.checked);
                        }
                    }}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 7}
                    isChecked={sendAttachment}
                    ariaLabel={translate("popUp.email.attachDocument")}
                    label={translate("popUp.email.attachDocument")}
                />

                <Checkbox
                    inputId="sendCopy"
                    onChange={(event) => {
                        event.stopPropagation();
                        const input = (event.currentTarget as HTMLElement).querySelector("input");
                        if (input) {
                            setSendCopy(input.checked);
                        }
                    }}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 8}
                    isChecked={sendCopy}
                    ariaLabel={translate("popUp.email.sendMeCopy", myEmail)}
                    label={translate("popUp.email.sendMeCopy", myEmail)}
                />
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 9}
                okEnabled={okEnabled}
                okCallback={handleSend}
                okText={translate("popUp.email.send")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default EmailModal;
